import { Box, Input, ValidationMessage } from '@modusign/design-system';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import { validateEmail } from '@app/libs/validation';

import './EmailAutoCompleteInput.scss';

const getUsername = (value: string) => value.split('@')[0];
const getDomain = (value: string) => value.split('@')[1];

const wrapperSx = {
  display: 'inline-block',
  width: '100%',
} as CSSProperties;

const menuSx = {
  width: '100%',
  borderRadius: '3px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
  background: 'rgba(255, 255, 255, 0.9)',
  padding: 0,
  fontSize: '90%',
  position: 'absolute',
  overflow: 'auto',
  zIndex: 100,
  left: 0,
  top: '40px',
} as CSSProperties;

type Suggestion = { id: string; label: string };

const defaultDomainSuggestions = [
  { id: 'naver', label: 'naver.com' },
  { id: 'gmail', label: 'gmail.com' },
  { id: 'hanmail', label: 'hanmail.net' },
  { id: 'daum', label: 'daum.net' },
  { id: 'nate', label: 'nate.com' },
];

interface EmailAutoCompleteInputProps {
  autoFocus?: boolean;
  domainSuggestions?: Suggestion[];
  error?: string;
  height?: string;
  initialValue: string;
  maxLength: number;
  id?: string;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  onBlur?: () => void;
  onClear?: () => void;
  onChange: (value: string) => void;
}

const defaultProps = {
  maxLength: CONTACT_INFO_MAX_LENGTH,
};

function filterSuggestions(domainSuggestions: Suggestion[], email: string): Suggestion[] {
  const typedInDomain = getDomain(email);
  const suggestions = [...domainSuggestions];

  if (process.env.REACT_APP_MODUSIGN_ENV !== 'production') {
    suggestions.unshift({ id: 'getnada', label: 'getnada.com' });
  }

  if (typedInDomain) {
    return suggestions.filter(
      ({ label }) => label.startsWith(typedInDomain) && label !== typedInDomain
    );
  }

  return suggestions;
}

// downshift의 한글 지원 때문에 uncontrolled input으로 사용합니다.
function EmailAutoCompleteInput({
  autoFocus,
  domainSuggestions = defaultDomainSuggestions,
  error,
  height,
  initialValue,
  maxLength,
  id,
  name,
  placeholder,
  readOnly,
  onBlur,
  onClear,
  onChange,
}: EmailAutoCompleteInputProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState(() =>
    filterSuggestions(domainSuggestions, initialValue)
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [setIsFocused, onBlur]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const onInputValueChange = useCallback(
    ({ inputValue }) => {
      setIsTouched(true);
      setSuggestions(filterSuggestions(domainSuggestions, inputValue));
      onChange(inputValue.trim());
    },
    [domainSuggestions, onChange]
  );

  const itemToString = (suggestion: Suggestion | null) =>
    suggestion ? `${getUsername(initialValue)}@${suggestion.label}` : '';

  const isOpen = isFocused && initialValue.length > 0 && initialValue[0] !== '@';

  const className = cx({
    InputSet: true,
    'InputSet--theme-editor': true,
    'InputSet--invalid': isTouched && error,
    EmailAutoCompleteInput: true,
  });

  const {
    getComboboxProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    setInputValue,
  } = useCombobox({
    items: suggestions,
    initialInputValue: initialValue,
    itemToString,
    isOpen,
    onInputValueChange,
  });

  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue, setInputValue]);

  return (
    <div className={className}>
      <Box sx={wrapperSx}>
        <div {...getComboboxProps()}>
          <Input
            height={height}
            isInvalid={error && error.length > 0}
            maxLength={maxLength}
            name={name}
            placeholder={placeholder}
            readOnly={readOnly}
            sx={{ pr: '30px' }}
            type="email"
            {...getInputProps({
              id,
              autoFocus,
              onBlur: handleBlur,
              onFocus: handleFocus,
            })}
          />
          <Box as="ul" sx={menuSx} {...getMenuProps()}>
            {isOpen &&
              !readOnly &&
              suggestions.map((item, index) => (
                <li
                  className={cx({
                    'EmailAutoCompleteInput__autocomplete-item': true,
                    'EmailAutoCompleteInput__autocomplete-item--selected':
                      highlightedIndex === index,
                  })}
                  key={item.id}
                  {...getItemProps({
                    item,
                    index,
                    onMouseDown: (e: ReactMouseEvent<HTMLLIElement, MouseEvent>) => {
                      e.stopPropagation();
                    },
                  })}
                >
                  {`${getUsername(initialValue)}@${item.label}`}
                </li>
              ))}
          </Box>
        </div>
      </Box>
      {initialValue && onClear && (
        <button
          aria-label="clear"
          className="InputSet__clear-button"
          type="button"
          onClick={() => {
            setInputValue('');
            onClear();
          }}
        />
      )}
      {error && (
        <ValidationMessage fontSize="sm" fontWeight="regular">
          {error}
        </ValidationMessage>
      )}
    </div>
  );
}

export function emailValidator(value?: string) {
  const validateResult = validateEmail(value);

  if (validateResult.success) {
    return true;
  }
  return validateResult.message;
}

EmailAutoCompleteInput.defaultProps = defaultProps;

export default EmailAutoCompleteInput;
